const root = document.documentElement;
const header = document.querySelector("[data-header]");
const hero = document.querySelector(".hero");
const portal = document.querySelector("[data-portal]");
const portalTracePaths = Array.from(document.querySelectorAll(".portal-logo-trace-path"));
const portalTraceGlows = Array.from(document.querySelectorAll(".portal-logo-trace-glow"));
const portalTracePen = document.querySelector(".portal-logo-pen");
const loader = document.querySelector("[data-loader]");
const menu = document.querySelector("[data-menu]");
const menuToggle = document.querySelector("[data-menu-toggle]");
const menuClose = document.querySelector("[data-menu-close]");
const revealItems = document.querySelectorAll(".reveal, .music-section .release-card");
const releaseCards = Array.from(document.querySelectorAll(".music-section .release-card"));
const releaseGrid = document.querySelector(".music-section .release-grid");
const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const mobileCards = window.matchMedia("(max-width: 720px)");

let scrollTicking = false;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const easeOut = (value) => 1 - Math.pow(1 - value, 3);
const easeInOut = (value) =>
  value < 0.5 ? 4 * value * value * value : 1 - Math.pow(-2 * value + 2, 3) / 2;
const normalizeToken = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const markIntroSeen = () => {
  try {
    window.sessionStorage.setItem("agletIntroPlayed", "true");
  } catch {
    // Browsers can block sessionStorage in strict contexts.
  }
  root.classList.add("intro-complete");
};

const setupIntro = () => {
  if (!loader || root.classList.contains("intro-seen") || reduceMotion.matches) {
    markIntroSeen();
    return;
  }

  window.setTimeout(markIntroSeen, 1700);
};

const setupExternalLinks = () => {
  document.querySelectorAll('a[href^="http"]').forEach((link) => {
    const url = new URL(link.href, window.location.href);

    if (url.origin === window.location.origin) {
      return;
    }

    link.target = "_blank";
    link.rel = "noopener noreferrer";
  });

  document.addEventListener("click", (event) => {
    const link = event.target.closest('a[href^="http"]');

    if (!link) {
      return;
    }

    const url = new URL(link.href, window.location.href);

    if (url.origin === window.location.origin) {
      return;
    }

    event.preventDefault();
    window.open(url.href, "_blank", "noopener,noreferrer");
  });
};

const updateVisualState = () => {
  const scrollY = window.scrollY || 0;

  header?.classList.toggle("is-scrolled", scrollY > 12);

  if (!reduceMotion.matches) {
    const heroHeight = hero?.offsetHeight || window.innerHeight;
    const progress = clamp(scrollY / Math.max(1, heroHeight * 0.42), 0, 1);
    const eased = easeOut(progress);

    root.style.setProperty("--hero-scale", (1.065 - eased * 0.035).toFixed(3));
    root.style.setProperty("--hero-lift", `${Math.round(eased * -18)}px`);
    root.style.setProperty("--hero-fade", (1 - eased * 0.32).toFixed(3));
    root.style.setProperty("--logo-crop", `${(7 - eased * 7).toFixed(2)}%`);
    root.style.setProperty("--logo-scale", (0.94 + eased * 0.08).toFixed(3));
    root.style.setProperty("--line-open", (0.12 + eased * 0.88).toFixed(3));
    root.style.setProperty("--line-opacity", (0.22 + eased * 0.68).toFixed(3));

    if (portal) {
      const portalStart = portal.offsetTop;
      const portalTravel = Math.max(1, portal.offsetHeight - window.innerHeight);
      const portalProgress = clamp((scrollY - portalStart) / portalTravel, 0, 1);
      const portalEased = easeOut(portalProgress);
      const writeProgress = easeOut(clamp((portalProgress - 0.02) / 0.34, 0, 1));
      const holdProgress = easeInOut(clamp((portalProgress - 0.32) / 0.34, 0, 1));
      const releaseProgress = easeOut(clamp((portalProgress - 0.68) / 0.24, 0, 1));
      const fadeProgress = easeOut(clamp((portalProgress - 0.975) / 0.025, 0, 1));
      const labelProgress = easeOut(clamp(portalProgress / 0.28, 0, 1));
      const sweepProgress = easeInOut(clamp((portalProgress - 0.05) / 0.82, 0, 1));
      const sweepVisible = sweepProgress > 0.02 && sweepProgress < 0.99 ? 1 : 0;
      const logoOpacity = clamp(writeProgress * 1.15 - fadeProgress * 0.55, 0, 1);
      const fillProgress = easeOut(clamp((portalProgress - 0.035) / 0.2, 0, 1));
      const traceOpacity = clamp(writeProgress * 1.35 - fadeProgress * 1.1, 0, 1);
      const orbitProgress = easeOut(clamp((portalProgress - 0.02) / 0.3, 0, 1));
      const pulseProgress = easeInOut(clamp((portalProgress - 0.08) / 0.74, 0, 1));

      root.style.setProperty("--portal-progress", portalEased.toFixed(3));
      root.style.setProperty("--portal-color-opacity", clamp(0.5 - portalEased * 0.18, 0.22, 0.5).toFixed(3));
      root.style.setProperty("--portal-bg-scale", (1.08 - releaseProgress * 0.04).toFixed(3));
      root.style.setProperty("--portal-bg-opacity", (0.72 + releaseProgress * 0.14).toFixed(3));
      root.style.setProperty("--portal-bg-blur", `${(1.2 - releaseProgress * 1.2).toFixed(2)}px`);
      root.style.setProperty("--portal-grid-opacity", (0.16 - releaseProgress * 0.08).toFixed(3));
      root.style.setProperty("--portal-scrim-opacity", (0.72 - releaseProgress * 0.26).toFixed(3));
      root.style.setProperty("--portal-art-scale", (1.04 - releaseProgress * 0.02).toFixed(3));
      root.style.setProperty("--portal-art-opacity", "0");
      root.style.setProperty("--portal-logo-write-stop", `${(writeProgress * 100).toFixed(2)}%`);
      root.style.setProperty("--portal-logo-fill-opacity", (fillProgress * 0.94).toFixed(3));
      root.style.setProperty("--portal-logo-stroke-offset", (1 - writeProgress).toFixed(3));
      root.style.setProperty("--portal-logo-trace-opacity", traceOpacity.toFixed(3));
      root.style.setProperty("--portal-logo-glow-opacity", (traceOpacity * 0.72).toFixed(3));
      root.style.setProperty("--portal-logo-pen-opacity", (sweepVisible * clamp(1 - fadeProgress * 2.4, 0, 1)).toFixed(3));
      root.style.setProperty("--portal-logo-pen-scale", (0.78 + sweepProgress * 0.34).toFixed(3));
      root.style.setProperty("--portal-logo-opacity", logoOpacity.toFixed(3));
      root.style.setProperty("--portal-logo-scale", (0.86 + holdProgress * 0.14).toFixed(3));
      root.style.setProperty("--portal-logo-y", `${Math.round(holdProgress * -10)}px`);
      root.style.setProperty("--portal-logo-sweep-left", `${(-12 + sweepProgress * 124).toFixed(2)}%`);
      root.style.setProperty("--portal-logo-sweep-opacity", (sweepVisible * clamp(1 - fadeProgress * 2.2, 0, 1)).toFixed(3));
      root.style.setProperty("--portal-orbit-opacity", (orbitProgress * clamp(1 - fadeProgress * 0.45, 0, 1)).toFixed(3));
      root.style.setProperty("--portal-orbit-shift", `${Math.round((portalEased - 0.5) * -36)}px`);
      root.style.setProperty("--portal-pulse-scale", (0.84 + pulseProgress * 0.22).toFixed(3));
      root.style.setProperty("--portal-pulse-opacity", (pulseProgress * clamp(1 - fadeProgress * 0.5, 0, 1)).toFixed(3));
      root.style.setProperty("--portal-label-opacity", clamp(1 - labelProgress * 1.1, 0, 1).toFixed(3));
      root.style.setProperty("--portal-label-lift", `${Math.round(labelProgress * 18)}px`);

      if (portalTracePaths.length) {
        const traceCount = portalTracePaths.length;
        const tracePosition = writeProgress * traceCount;
        let activeTrace = portalTracePaths[Math.min(traceCount - 1, Math.floor(tracePosition))];
        let activeLocalProgress = 0;

        portalTracePaths.forEach((path, index) => {
          const localProgress = clamp(tracePosition - index, 0, 1);
          path.style.strokeDashoffset = (1 - localProgress).toFixed(3);
          if (localProgress > 0 && localProgress < 1) {
            activeTrace = path;
            activeLocalProgress = localProgress;
          } else if (localProgress === 1) {
            activeLocalProgress = 1;
          }
        });

        portalTraceGlows.forEach((path, index) => {
          const localProgress = clamp(tracePosition - index, 0, 1);
          path.style.strokeDashoffset = (1 - localProgress).toFixed(3);
        });

        if (activeTrace && portalTracePen) {
          const traceLength = activeTrace.getTotalLength();
          const point = activeTrace.getPointAtLength(traceLength * activeLocalProgress);
          portalTracePen.setAttribute("cx", point.x.toFixed(2));
          portalTracePen.setAttribute("cy", point.y.toFixed(2));
        }
      }
    }

    if (releaseCards.length && releaseGrid) {
      if (mobileCards.matches) {
        const viewportHeight = window.innerHeight || 1;
        const gridTop = releaseGrid.getBoundingClientRect().top + scrollY;

        releaseCards.forEach((card, index) => {
          const cardStart = gridTop + card.offsetTop;
          const stackProgress = easeInOut(
            clamp((scrollY + viewportHeight * 0.36 - cardStart) / (viewportHeight * 0.58), 0, 1)
          );
          const sleeveLift = Math.round(stackProgress * -(20 + index * 2));

          card.style.setProperty("--mobile-stack-progress", stackProgress.toFixed(3));
          card.style.setProperty("--mobile-stack-lift", `${sleeveLift}px`);
          card.style.setProperty("--mobile-stack-scale", (1 - stackProgress * 0.065).toFixed(3));
          card.style.setProperty("--mobile-stack-tilt", `${(stackProgress * -13).toFixed(2)}deg`);
          card.style.setProperty("--mobile-stack-brightness", (1 - stackProgress * 0.14).toFixed(3));
          card.style.setProperty("--mobile-stack-saturation", (1 + stackProgress * 0.06).toFixed(3));
        });
      } else {
        releaseCards.forEach((card) => {
          card.style.removeProperty("--mobile-stack-progress");
          card.style.removeProperty("--mobile-stack-lift");
          card.style.removeProperty("--mobile-stack-scale");
          card.style.removeProperty("--mobile-stack-tilt");
          card.style.removeProperty("--mobile-stack-brightness");
          card.style.removeProperty("--mobile-stack-saturation");
        });
      }
    }
  }

  scrollTicking = false;
};

const requestVisualUpdate = () => {
  if (scrollTicking) {
    return;
  }

  scrollTicking = true;
  window.requestAnimationFrame(updateVisualState);
};

const setMenuState = (isOpen) => {
  if (!menu || !menuToggle) {
    return;
  }

  menu.classList.toggle("is-open", isOpen);
  menu.setAttribute("aria-hidden", String(!isOpen));
  menuToggle.setAttribute("aria-expanded", String(isOpen));
  menuToggle.setAttribute("aria-label", isOpen ? "Close menu" : "Open menu");
  document.body.classList.toggle("has-menu", isOpen);

  if (isOpen) {
    menu.querySelector("a")?.focus({ preventScroll: true });
  } else {
    menuToggle.focus({ preventScroll: true });
  }
};

const setupMenu = () => {
  menuToggle?.addEventListener("click", () => {
    setMenuState(!menu?.classList.contains("is-open"));
  });

  menuClose?.addEventListener("click", () => setMenuState(false));

  menu?.querySelectorAll("a").forEach((link) => {
    link.addEventListener("click", () => setMenuState(false));
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && menu?.classList.contains("is-open")) {
      setMenuState(false);
    }
  });
};

const setupHistoryBackLinks = () => {
  document.querySelectorAll("[data-history-back]").forEach((link) => {
    link.addEventListener("click", (event) => {
      if (window.history.length <= 1) {
        return;
      }

      event.preventDefault();
      window.history.back();
    });
  });
};

const setupReveals = () => {
  if (!revealItems.length) {
    return;
  }

  if (reduceMotion.matches || !("IntersectionObserver" in window)) {
    revealItems.forEach((item) => item.classList.add("is-visible"));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.18 }
  );

  revealItems.forEach((item) => observer.observe(item));
};

const setupProductForms = () => {
  document.querySelectorAll("[data-product-root]").forEach((productRoot) => {
    const variantsNode = productRoot.querySelector("[data-product-variants]");
    const variantInput = productRoot.querySelector("[data-variant-id-input]");
    const priceNode = productRoot.querySelector("[data-product-price]");
    const addButton = productRoot.querySelector("[data-add-to-cart]");
    const noteNode = productRoot.querySelector("[data-product-note]");
    const mainImage = productRoot.querySelector("[data-product-main-image]");
    const mediaTrack = productRoot.querySelector("[data-product-media-track]");
    const slides = Array.from(productRoot.querySelectorAll("[data-product-slide]"));
    const optionGroups = Array.from(productRoot.querySelectorAll("[data-product-option]"));
    const thumbs = Array.from(productRoot.querySelectorAll("[data-gallery-thumb]"));
    const zoomButton = productRoot.querySelector("[data-product-zoom]");
    const lightbox = productRoot.querySelector("[data-product-lightbox]");
    const lightboxImage = productRoot.querySelector("[data-product-lightbox-image]");
    const lightboxCount = productRoot.querySelector("[data-product-lightbox-count]");
    const lightboxCloseButtons = Array.from(productRoot.querySelectorAll("[data-product-lightbox-close]"));
    const lightboxPrev = productRoot.querySelector("[data-product-lightbox-prev]");
    const lightboxNext = productRoot.querySelector("[data-product-lightbox-next]");

    if (!variantsNode || !variantInput || !priceNode || !addButton || !noteNode) {
      return;
    }

    let variants = [];
    let activeImageIndex = 0;
    let suppressMediaScrollSyncUntil = 0;
    let suppressMediaScrollSyncTimer;
    let programmaticMediaTarget = "";
    let userMediaScrollSyncUntil = 0;
    let activeImageReinforcementTimers = [];

    try {
      variants = JSON.parse(variantsNode.textContent || "[]");
    } catch {
      return;
    }

    const getSelectedOptions = () =>
      optionGroups.map((group) => {
        const select = group.querySelector("select[data-option-value]");
        if (select) {
          return select.value || "";
        }

        const checked = group.querySelector("input[data-option-value]:checked");
        return checked?.value || "";
      });

    const setSelectedOptions = (options) => {
      optionGroups.forEach((group, index) => {
        const value = options[index];
        const select = group.querySelector("select[data-option-value]");

        if (select) {
          select.value = value;
          return;
        }

        const input = Array.from(group.querySelectorAll("input[data-option-value]")).find(
          (optionInput) => optionInput.value === value
        );

        if (input) {
          input.checked = true;
        }
      });
    };

    const findVariant = () => {
      const selectedOptions = getSelectedOptions();
      return variants.find((variant) =>
        variant.options.every((optionValue, index) => optionValue === selectedOptions[index])
      );
    };

    const findColorOptionIndex = () =>
      optionGroups.findIndex((group) => /color|colour/i.test(group.dataset.optionName || ""));

    const findSlideForOptionValue = (value) => {
      const normalizedValue = normalizeToken(value);

      if (!normalizedValue) {
        return null;
      }

      return slides.find((slide) => {
        const optionColor = normalizeToken(slide.dataset.optionColor || "");
        if (optionColor && optionColor === normalizedValue) {
          return true;
        }

        const searchable = normalizeToken(
          `${slide.dataset.imageAlt || ""} ${slide.dataset.imageSrc || ""}`
        );

        return searchable.includes(normalizedValue);
      });
    };

    const getImageKey = (src, imageId) => {
      if (imageId) {
        return `id:${imageId}`;
      }

      return `src:${src || ""}`;
    };

    const updateOptionAvailability = () => {
      optionGroups.forEach((group, groupIndex) => {
        const select = group.querySelector("select[data-option-value]");

        if (!select) {
          return;
        }

        Array.from(select.options).forEach((option) => {
          const hasAvailableVariant = variants.some(
            (variant) => variant.available && variant.options[groupIndex] === option.value
          );

          option.disabled = !hasAvailableVariant;
        });
      });
    };

    const syncVariantFromImage = (imageSrc, imageId, optionColor = "") => {
      const colorIndex = findColorOptionIndex();

      if (optionColor && colorIndex >= 0) {
        const selectedOptions = getSelectedOptions();
        const preferredOptions = [...selectedOptions];
        preferredOptions[colorIndex] = optionColor;
        const exactVariant = variants.find((variant) =>
          variant.options.every((optionValue, index) => optionValue === preferredOptions[index])
        );
        const colorVariant =
          exactVariant ||
          variants.find((variant) => variant.available && variant.options[colorIndex] === optionColor) ||
          variants.find((variant) => variant.options[colorIndex] === optionColor);

        if (colorVariant) {
          setSelectedOptions(colorVariant.options);
          updateVariant({ syncImage: false });
          return;
        }
      }

      const imageVariant = variants.find((variant) =>
        (imageId && String(variant.imageId) === String(imageId)) ||
        variant.featuredImage === imageSrc
      );

      if (imageVariant) {
        setSelectedOptions(imageVariant.options);
        updateVariant({ syncImage: false });
      }
    };

    const setActiveImageState = (src, imageId) => {
      thumbs.forEach((thumb) => {
        const isActive =
          (imageId && thumb.dataset.imageId === String(imageId)) ||
          (!imageId && thumb.dataset.imageSrc === src);

        thumb.classList.toggle("is-active", Boolean(isActive));
      });

      const nextIndex = slides.findIndex((slide) =>
        (imageId && slide.dataset.imageId === String(imageId)) ||
        (!imageId && slide.dataset.imageSrc === src)
      );

      if (nextIndex >= 0) {
        activeImageIndex = nextIndex;
      }
    };

    const reinforceActiveImageState = (src, imageId, targetKey) => {
      activeImageReinforcementTimers.forEach((timer) => window.clearTimeout(timer));
      activeImageReinforcementTimers = [140, 620, 1240].map((delay) =>
        window.setTimeout(() => {
          if (programmaticMediaTarget === targetKey && Date.now() > userMediaScrollSyncUntil) {
            setActiveImageState(src, imageId);
          }
        }, delay)
      );
    };

    const updateLightboxImage = (index) => {
      if (!lightboxImage || !slides.length) {
        return;
      }

      activeImageIndex = (index + slides.length) % slides.length;
      const activeSlide = slides[activeImageIndex];
      const imageSrc = activeSlide.dataset.imageSrc;
      const imageAlt = activeSlide.dataset.imageAlt || "";

      lightboxImage.src = imageSrc;
      lightboxImage.alt = imageAlt;

      if (lightboxCount) {
        lightboxCount.textContent = `${activeImageIndex + 1} / ${slides.length}`;
      }
    };

    const openLightbox = (index = activeImageIndex) => {
      if (!lightbox || !lightboxImage || !slides.length) {
        return;
      }

      updateLightboxImage(index);
      lightbox.classList.add("is-open");
      lightbox.setAttribute("aria-hidden", "false");
      document.body.classList.add("has-lightbox");
      lightboxCloseButtons[0]?.focus({ preventScroll: true });
    };

    const closeLightbox = () => {
      if (!lightbox) {
        return;
      }

      lightbox.classList.remove("is-open");
      lightbox.setAttribute("aria-hidden", "true");
      document.body.classList.remove("has-lightbox");
      zoomButton?.focus({ preventScroll: true });
    };

    const setMainImage = (src, imageId, options = {}) => {
      if (!src) {
        return;
      }

      const nextSlide = slides.find((slide) =>
        (imageId && slide.dataset.imageId === String(imageId)) ||
        slide.dataset.imageSrc === src
      );

      if (nextSlide && mediaTrack) {
        window.clearTimeout(suppressMediaScrollSyncTimer);
        suppressMediaScrollSyncUntil = Date.now() + 900;
        programmaticMediaTarget = getImageKey(nextSlide.dataset.imageSrc, nextSlide.dataset.imageId);
        reinforceActiveImageState(nextSlide.dataset.imageSrc, nextSlide.dataset.imageId, programmaticMediaTarget);
        suppressMediaScrollSyncTimer = window.setTimeout(() => {
          suppressMediaScrollSyncUntil = 0;
        }, 960);

        mediaTrack.scrollTo({
          left: nextSlide.offsetLeft,
          behavior: options.instant ? "auto" : "smooth",
        });
      } else if (mainImage) {
        mainImage.src = src;
      }

      setActiveImageState(src, imageId);

      if (options.syncVariant) {
        syncVariantFromImage(src, imageId, nextSlide?.dataset.optionColor);
      }
    };

    const updateVariant = (options = {}) => {
      const variant = findVariant();

      if (!variant) {
        addButton.disabled = true;
        addButton.textContent = "Unavailable";
        noteNode.textContent = "That combination is not available.";
        return;
      }

      variantInput.value = variant.id;
      priceNode.textContent = variant.price;
      addButton.disabled = !variant.available;
      addButton.textContent = variant.available ? "Add to Cart" : "Sold Out";
      noteNode.textContent = variant.available ? "" : "This option is currently sold out.";

      if (options.syncImage !== false) {
        const colorIndex = findColorOptionIndex();
        const selectedOptions = getSelectedOptions();
        const colorSlide = colorIndex >= 0 ? findSlideForOptionValue(selectedOptions[colorIndex]) : null;

        if (variant.featuredImage) {
          setMainImage(variant.featuredImage, variant.imageId);
        } else if (colorSlide) {
          setMainImage(colorSlide.dataset.imageSrc, colorSlide.dataset.imageId);
        }
      }
    };

    optionGroups.forEach((group) => {
      group.addEventListener("change", updateVariant);
    });

    thumbs.forEach((thumb) => {
      thumb.addEventListener("click", () => {
        const nextImage = thumb.dataset.imageSrc;
        setMainImage(nextImage, thumb.dataset.imageId, {
          syncVariant: true,
          optionColor: thumb.dataset.optionColor,
        });
      });
    });

    zoomButton?.addEventListener("click", () => openLightbox());

    slides.forEach((slide, index) => {
      slide.addEventListener("click", () => openLightbox(index));
    });

    lightboxCloseButtons.forEach((button) => {
      button.addEventListener("click", closeLightbox);
    });

    lightboxPrev?.addEventListener("click", () => updateLightboxImage(activeImageIndex - 1));
    lightboxNext?.addEventListener("click", () => updateLightboxImage(activeImageIndex + 1));

    document.addEventListener("keydown", (event) => {
      if (!lightbox?.classList.contains("is-open")) {
        return;
      }

      if (event.key === "Escape") {
        closeLightbox();
      } else if (event.key === "ArrowLeft") {
        updateLightboxImage(activeImageIndex - 1);
      } else if (event.key === "ArrowRight") {
        updateLightboxImage(activeImageIndex + 1);
      }
    });

    if (mediaTrack && slides.length > 1) {
      let mediaScrollTicking = false;

      const clearProgrammaticMediaTarget = () => {
        programmaticMediaTarget = "";
        suppressMediaScrollSyncUntil = 0;
        userMediaScrollSyncUntil = Date.now() + 1400;
        window.clearTimeout(suppressMediaScrollSyncTimer);
        activeImageReinforcementTimers.forEach((timer) => window.clearTimeout(timer));
        activeImageReinforcementTimers = [];
      };

      const syncImageFromScroll = () => {
        const trackLeft = mediaTrack.getBoundingClientRect().left;
        const closestSlide = slides.reduce((closest, slide) => {
          const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
          return distance < closest.distance ? { slide, distance } : closest;
        }, { slide: slides[0], distance: Number.POSITIVE_INFINITY }).slide;

        if (closestSlide) {
          if (Date.now() > userMediaScrollSyncUntil) {
            mediaScrollTicking = false;
            return;
          }

          const closestKey = getImageKey(closestSlide.dataset.imageSrc, closestSlide.dataset.imageId);

          if (programmaticMediaTarget) {
            if (closestKey === programmaticMediaTarget) {
              setActiveImageState(closestSlide.dataset.imageSrc, closestSlide.dataset.imageId);
              programmaticMediaTarget = "";
              suppressMediaScrollSyncUntil = 0;
              window.clearTimeout(suppressMediaScrollSyncTimer);
            }

            mediaScrollTicking = false;
            return;
          }

          if (Date.now() < suppressMediaScrollSyncUntil) {
            mediaScrollTicking = false;
            return;
          }

          setActiveImageState(closestSlide.dataset.imageSrc, closestSlide.dataset.imageId);

          syncVariantFromImage(
            closestSlide.dataset.imageSrc,
            closestSlide.dataset.imageId,
            closestSlide.dataset.optionColor
          );
        }

        mediaScrollTicking = false;
      };

      mediaTrack.addEventListener("scroll", () => {
        if (mediaScrollTicking) {
          return;
        }

        mediaScrollTicking = true;
        window.requestAnimationFrame(syncImageFromScroll);
      }, { passive: true });

      mediaTrack.addEventListener("pointerdown", clearProgrammaticMediaTarget, { passive: true });
      mediaTrack.addEventListener("touchstart", clearProgrammaticMediaTarget, { passive: true });
      mediaTrack.addEventListener("wheel", clearProgrammaticMediaTarget, { passive: true });
    }

    updateOptionAvailability();
    updateVariant();
  });
};

const setupNewsletterForms = () => {
  document.querySelectorAll(".signup-form").forEach((form) => {
    form.addEventListener("submit", async (event) => {
      if (form.matches("[action*='/contact']")) {
        try {
          window.sessionStorage.setItem("agletNewsletterReturn", "true");
          window.sessionStorage.setItem("agletIntroPlayed", "true");
        } catch {
          // Browsers can block sessionStorage in strict contexts.
        }

        const button = form.querySelector("button[type='submit']");
        if (button) {
          button.disabled = true;
          button.textContent = "Joining";
        }

        return;
      }

      if (!window.fetch || form.dataset.isSubmitting === "true") {
        return;
      }

      event.preventDefault();
      form.dataset.isSubmitting = "true";

      const note = form.querySelector(".form-note");
      const button = form.querySelector("button[type='submit']");
      const originalButtonText = button?.textContent || "";

      if (button) {
        button.disabled = true;
        button.textContent = "Joining";
      }

      try {
        const response = await fetch(form.action, {
          method: "POST",
          body: new FormData(form),
          credentials: "same-origin",
          headers: {
            Accept: "text/html",
          },
        });

        if (!response.ok) {
          throw new Error("Newsletter submit failed");
        }

        if (note) {
          note.classList.remove("form-note-error");
          note.classList.add("form-note-success");
          note.textContent = "You are in. Watch for the next Aglet drop.";
        }

        form.reset();
      } catch {
        if (note) {
          note.classList.remove("form-note-success");
          note.classList.add("form-note-error");
          note.textContent = "That did not go through. Try again in a second.";
        }
      } finally {
        if (button) {
          button.disabled = false;
          button.textContent = originalButtonText;
        }
        form.dataset.isSubmitting = "false";
      }
    });
  });
};

const setupNewsletterReturn = () => {
  const params = new URLSearchParams(window.location.search);
  const isPosted =
    params.get("customer_posted") === "true" ||
    params.get("contact_posted") === "true";
  let wasNewsletterSubmit = false;

  try {
    wasNewsletterSubmit = window.sessionStorage.getItem("agletNewsletterReturn") === "true";
  } catch {
    wasNewsletterSubmit = false;
  }

  if (!isPosted && !wasNewsletterSubmit) {
    return;
  }

  root.classList.add("newsletter-return", "intro-seen", "intro-complete");

  const target =
    document.querySelector("#join") ||
    document.querySelector("#email-list") ||
    document.querySelector("#contact_form");

  if (target) {
    window.requestAnimationFrame(() => {
      target.scrollIntoView({ block: "start", behavior: "auto" });
    });
  }

  if (isPosted && window.history.replaceState) {
    window.history.replaceState(null, "", `${window.location.pathname}#join`);
  }

  try {
    window.sessionStorage.removeItem("agletNewsletterReturn");
  } catch {
    // Browsers can block sessionStorage in strict contexts.
  }
};

setupNewsletterReturn();
setupIntro();
setupExternalLinks();
setupMenu();
setupHistoryBackLinks();
setupReveals();
setupProductForms();
setupNewsletterForms();
updateVisualState();

window.addEventListener("scroll", requestVisualUpdate, { passive: true });
window.addEventListener("resize", requestVisualUpdate);

if (typeof reduceMotion.addEventListener === "function") {
  reduceMotion.addEventListener("change", () => {
    markIntroSeen();
    revealItems.forEach((item) => item.classList.add("is-visible"));
    requestVisualUpdate();
  });
}
